import type { ProgressMap } from '@/hooks/useProgress';

export interface StudentLevel {
  index: number;
  name: string;
  icon: string;
  minXp: number;
  nextXp: number | null;
}

const LEVELS: StudentLevel[] = [
  { index: 1, name: 'Primeiros passos', icon: 'ri-seedling-line', minXp: 0, nextXp: 50 },
  { index: 2, name: 'Explorador da pauta', icon: 'ri-compass-3-line', minXp: 50, nextXp: 150 },
  { index: 3, name: 'Leitor iniciante', icon: 'ri-book-open-line', minXp: 150, nextXp: 300 },
  { index: 4, name: 'Leitor em evolução', icon: 'ri-bar-chart-line', minXp: 300, nextXp: 600 },
  { index: 5, name: 'Leitor seguro', icon: 'ri-medal-line', minXp: 600, nextXp: 1000 },
  { index: 6, name: 'Mestre da fixação', icon: 'ri-trophy-line', minXp: 1000, nextXp: null },
];

export interface GamificationSummary {
  xp: number;
  totalCorrect: number;
  totalWrong: number;
  masteredLessons: number;
  level: StudentLevel;
  levelPercent: number;
  xpToNext: number;
}

export function calculateGamification(progress: ProgressMap): GamificationSummary {
  const entries = Object.values(progress);
  const totalCorrect = entries.reduce((sum, item) => sum + (item.correct ?? 0), 0);
  const totalWrong = entries.reduce((sum, item) => sum + (item.wrong ?? 0), 0);
  const masteredLessons = entries.filter((item) => item.mastered).length;

  // XP é a recompensa de jogo e deve reagir visivelmente a cada acerto.
  // O domínio percentual continua sendo a trava pedagógica independente.
  const xp = totalCorrect * 5 + masteredLessons * 120;

  let level = LEVELS[0];
  for (const candidate of LEVELS) {
    if (xp >= candidate.minXp) level = candidate;
  }

  const nextXp = level.nextXp;
  const levelPercent =
    nextXp === null
      ? 100
      : Math.max(
          0,
          Math.min(
            100,
            Math.round(((xp - level.minXp) / Math.max(1, nextXp - level.minXp)) * 100),
          ),
        );

  return {
    xp,
    totalCorrect,
    totalWrong,
    masteredLessons,
    level,
    levelPercent,
    xpToNext: nextXp === null ? 0 : Math.max(0, nextXp - xp),
  };
}
